

























































import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { Loader } from "@googlemaps/js-api-loader";
import { createImageOverlay } from "./image-overlay";
import { loadByUuid, generateImages } from "./image-service";
import { PicassoCollection, PicassoPicture } from "./types";
import AgroIcons from "@/components/agroIcons/AgroIcons.vue";
import AgroRatingIcon from "@/components/agroRatingList/agro-rating-icon.vue";

type ImageOverlayCtor = ReturnType<typeof createImageOverlay>;
type ImageOverlayInstance = InstanceType<ImageOverlayCtor>;

@Component({
  name: "ImagesTab",
  components: {
    AgroIcons,
    AgroRatingIcon,
  },
})
export default class ImagesTab extends Vue {
  @Prop({ type: Object, required: true })
  private map!: google.maps.Map | null;

  @Prop(String)
  private farmAnalysisUuid?: string;

  @Prop({ type: Number, required: false, default: 20000 })
  private imageZIndex?: number;

  private dayFormatter = new Intl.DateTimeFormat("pt-BR", { day: "numeric" });
  private monthFormatter = new Intl.DateTimeFormat("pt-BR", { month: "short" });

  public collection: PicassoCollection = new PicassoCollection();

  private ImageOverlay?: ImageOverlayCtor;

  private images: Record<string, ImageOverlayInstance> = {};
  public currentPicture?: PicassoPicture;
  public active = false;
  
  public readonly FILTERS = {
    RGB: 'rgb',
    FC1: 'fc-1',
    FC2: 'fc-2',
    ALL: 'ALL'
  } as const;
  public filter = this.FILTERS.ALL;
  
  async mounted() {
    if (this.farmAnalysisUuid) {
      try {
        this.collection = await this.loadCollection();
      } catch (err) {
        console.error("Error loading or generating images:", err);
        return;
      }
    }

    await this.ensureMapsLoaded();
    this.placeControl();

    if (this.collection.isProcessing) {
      await this.waitForProcessingToComplete();
    }
  }

  @Watch("imageZIndex")
  public async watchImageZIndex() {
    this.clear();
    this.images = {};

    const previousPicture = this.currentPicture;
    this.currentPicture = undefined;

    if (previousPicture) {
      await this.setImage(previousPicture);
    } else {
      this.$forceUpdate();
    }
  }

  private async ensureMapsLoaded() {
    if (this.ImageOverlay) {
      return;
    }
    this.ImageOverlay = createImageOverlay(window.google);
  }

  async loadCollection() {
    try {
      return await loadByUuid(this.farmAnalysisUuid!!);
    } catch {
      const res = await generateImages(this.farmAnalysisUuid!!);
      if (res) return await loadByUuid(this.farmAnalysisUuid!!);
      throw new Error("Failed to load or generate images");
    }
  }

  async waitForProcessingToComplete() {
    while (this.collection.isProcessing) {
      await new Promise((resolve) => setTimeout(resolve, 5000));
      this.collection = await loadByUuid(this.farmAnalysisUuid!!);
    }
  }

  get pictures() {
    const pics = this.collection.pictures;
    pics.sort((a, b) => b.sceneDate.getTime() - a.sceneDate.getTime());
    return pics;
  }

  monthAndDay(picture: PicassoPicture): string {
    return `${this.dayFormatter.format(picture.sceneDate)}/${this.monthFormatter.format(picture.sceneDate)}`;
  }

  isSelectedPicture(picture: PicassoPicture) {
    return picture === this.currentPicture;
  }

  async setImage(picture: PicassoPicture) {
    if (!picture.isSuccess) return;

    await this.ensureMapsLoaded();

    this.clear();

    if (this.currentPicture === picture) {
      this.currentPicture = undefined;
    } else {
      let overlay = this.images[picture.link];

      if (!overlay && this.collection.boundingBox && this.ImageOverlay && this.map) {
        overlay = this.createAndAttachOverlay(picture.link, this.collection.boundingBox);
        this.images[picture.link] = overlay;
      }

      overlay?.show();
      this.currentPicture = picture;
    }

    this.$forceUpdate();
  }

  private createAndAttachOverlay(imgUrl: string, boundingBox: any): ImageOverlayInstance {
    // boundingBox: GeoJSON Polygon [ [ [lon, lat], ... ] ]
    const coords = boundingBox.coordinates[0];
    const sw = new window.google.maps.LatLng(coords[0][1], coords[0][0]);
    const ne = new window.google.maps.LatLng(coords[1][1], coords[2][0]);
    const bounds = new window.google.maps.LatLngBounds(sw, ne);

    const Overlay = this.ImageOverlay!; // garantido após ensureMapsLoaded
    const overlay = new Overlay(bounds, imgUrl, this.imageZIndex);
    overlay.setMap(this.map!);
    return overlay;
  }

  private clear() {
    for (const value of Object.values(this.images)) {
      value.hide();
    }
  }

  public handleActive() {
    this.clear();
    this.active = !this.active;
    this.filter = this.FILTERS.ALL;
  }
  
  public setFilter(filter: any) {
    this.filter = filter;
    this.active = true;
  }
  
  get shouldShowProductFilter() {
    return this.collection && this.collection.hasMultipleProducts;
  }
  
  private getProductType(picture: PicassoPicture) {
    return picture && picture.product ? picture.product.type : '';
  }

  private placeControl() {
    if (!this.map) return;
    // window.google existe após ensureMapsLoaded()
    this.map.controls[window.google.maps.ControlPosition.LEFT_BOTTOM].push(
      this.$refs.imagesContainer as Node
    );
  }
}
